import type { NumericPoint } from "@incytestudios/ddv";
import type { ScenarioMap } from "@/components/types";
import { getFrequencyRatio } from "@/libs/calculations/pumps/pumpData";
import { getDensityCorrectionRatio } from "@/libs/charting/_common/densityCorrection";
import { getSimplifiedProducedTdh } from "..";

export const getMultiScenarioProducedTdhDensityCorrectionRatio = (
  scenario: ScenarioMap[string],
  areScenariosEspShore: boolean
): number | undefined => {
  const {
    pumpCalculations,
    ascendingOrderPumps,
    dischargeFluidDensityLBMFT3,
    desiredLiquidRateSTDB
  } = scenario.calcs;
  const operatingRPM = pumpCalculations.aggregate.operatingRPM.value;

  if (!ascendingOrderPumps.value.length || !Number.isFinite(operatingRPM)) {
    return undefined;
  }

  const graphMax = Math.min(
    ...ascendingOrderPumps.value.map(
      xrefPump => pumpCalculations.individual.value[xrefPump.id].graphMax
    )
  );

  if (!Number.isFinite(graphMax) || graphMax <= 0) {
    return undefined;
  }
  const pumpDischargeFlowRateBBLD =
    pumpCalculations.aggregate.dischargeFlowRateSTBD.value;
  const frequencyRatio = getFrequencyRatio(operatingRPM, areScenariosEspShore);
  const hypotheticalFlowRates = [
    ...Array.from({ length: 40 }, (_, i) => (graphMax / 40) * (i + 1)),
    desiredLiquidRateSTDB.value,
    pumpDischargeFlowRateBBLD
  ].sort((a, b) => a - b);

  hypotheticalFlowRates.map<NumericPoint>(flowRateBblD => {
    const data = getSimplifiedProducedTdh(
      flowRateBblD,
      frequencyRatio,
      ascendingOrderPumps.value,
      pumpCalculations.aggregate.gasHeadRerateRatio.value,
      scenario.parameters?.pump_wear_head_correction_ratio || 1,
      dischargeFluidDensityLBMFT3.value
    );

    return {
      x: flowRateBblD,
      y: areScenariosEspShore ? data.tdh : data.tdhPressure
    };
  });

  const scenarioRequiredTargetTDH =
    pumpCalculations.aggregate.requiredTotalDynamicHeadFt.value;
  const scenarioRequiredTargetPressurePSIA =
    pumpCalculations.aggregate.differentialPressurePSIA.value;
  const scenarioRequiredTargetBHP =
    pumpCalculations.aggregate.requiredPumpBrakeHP.value;

  const densityCorrectionRatios = getDensityCorrectionRatio(
    pumpDischargeFlowRateBBLD,
    ascendingOrderPumps.value,
    pumpCalculations.aggregate.gasHeadRerateRatio.value,
    scenario.parameters?.pump_wear_head_correction_ratio || 1,
    scenario.parameters?.pump_wear_brake_horsepower_correction_ratio || 1,
    dischargeFluidDensityLBMFT3.value,
    frequencyRatio,
    scenarioRequiredTargetTDH,
    scenarioRequiredTargetPressurePSIA,
    scenarioRequiredTargetBHP
  );

  // In multiscenario we're only ever correcting produced points so we can
  // always just look as ESP vs HPS to see which ratio we need to apply
  return areScenariosEspShore
    ? densityCorrectionRatios.densityCorrectionTDHRatio
    : densityCorrectionRatios.densityCorrectionPressureRatio;
};
